import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useInView } from 'react-intersection-observer';
import { CallIntelTrialProductTeaser } from '@frontend/call-intel-assets';
import { generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { pendo } from '@frontend/tracking';
import { useEligibility } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';

interface CallIntelExpiredTrialNudgeProps {
  locationIds: string[];
  onSubscribe: () => void;
  onDismiss?: () => void;
}

export const CallIntelExpiredTrialNudge = ({
  locationIds,
  onSubscribe,
  onDismiss,
}: CallIntelExpiredTrialNudgeProps) => {
  const { t } = useTranslation('call-intelligence');
  const [isVisible, setIsVisible] = useState(true);

  const { ref, inView } = useInView({ triggerOnce: true });

  const { shouldShow, recordDismiss } = useEligibility({
    featureId: 'call-intel-expired-trial-nudge',
    defaultCriteria: {
      strategy: 'any',
      conditions: [
        {
          type: 'view-count',
          maxViews: 2,
        },
        {
          type: 'dismiss-cooldown',
          maxDismisses: 2,
          cooldownDays: 7,
        },
      ],
    },
  });

  const handleDismiss = () => {
    setIsVisible(false);
    recordDismiss();
    onDismiss?.();
  };

  const shouldRender = shouldShow && isVisible;

  useEffect(() => {
    if (!inView) {
      return;
    }

    pendo.track(
      generateTrackingId({
        component: 'call-intel-expired-trial-nudge',
        context: 'view',
      }),
      {
        locationCount: locationIds.length,
      }
    );
  }, [inView, locationIds.length]);

  return createPortal(
    <AnimatePresence>
      {shouldRender && (
        <motion.aside
          ref={ref}
          css={styles.container}
          role='dialog'
          aria-labelledby='call-intel-expired-trial-nudge-title'
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <div css={styles.contentWrapper}>
            <CallIntelTrialProductTeaser height={32} width={32} css={styles.icon} />
            <div css={styles.textWrapper}>
              <Text size='medium' weight='bold' id='call-intel-expired-trial-nudge-title'>
                {t('Bring Call Intelligence back to your {{count}} location', {
                  count: locationIds.length,
                })}
              </Text>

              <Text size='medium'>
                {locationIds.length === 1
                  ? t(
                      "Your free trial has ended, but your calls haven't stopped. Subscribe to keep the insights flowing."
                    )
                  : t(
                      "Your free trials have ended, but your calls haven't stopped. Subscribe to keep the insights flowing."
                    )}
              </Text>
            </div>
          </div>

          <div css={styles.actions}>
            <Button
              size='small'
              variant='tertiary'
              onClick={handleDismiss}
              trackingId={generateTrackingId({
                component: 'call-intel-expired-trial-nudge',
                context: 'dismiss-button-click',
              })}
            >
              <Text size='medium' color='subdued' weight='bold'>
                {t('Dismiss')}
              </Text>
            </Button>

            <Button
              size='small'
              variant='tertiary'
              onClick={onSubscribe}
              trackingId={generateTrackingId({
                component: 'call-intel-expired-trial-nudge',
                context: 'subscribe-button-click',
              })}
            >
              {t('Subscribe')}
            </Button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>,
    document.body
  );
};

const styles = {
  container: css`
    position: fixed;
    right: var(--fabric-number-n-10-px);
    bottom: var(--fabric-number-n-10-px);
    z-index: ${theme.zIndex.high};
    width: min(336px, calc(100vw - 2 * var(--fabric-number-n-10-px)));
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
    padding: var(--fabric-space-padding-md);
    border: 1px solid var(--fabric-color-accent-seaweed-base);
    border-radius: var(--fabric-number-n-1-px);
    background: linear-gradient(
        var(--fabric-color-accent-seaweed-alpha-subtle),
        var(--fabric-color-accent-seaweed-alpha-subtle)
      ),
      var(--fabric-color-surface-raised);
    box-shadow: ${theme.shadows.floating};
  `,
  header: css`
    display: flex;
    align-items: flex-start;
    gap: var(--fabric-space-gap-xs);
  `,
  contentWrapper: css`
    display: flex;
    align-items: flex-start;
    gap: var(--fabric-space-gap-xs);
  `,
  textWrapper: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
  `,
  icon: css`
    min-height: 32px;
    min-width: 32px;
  `,
  actions: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--fabric-space-gap-md);

    button {
      padding: 0px;
    }
  `,
};
